import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Bell, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { shareThunk } from '@/features/selectedVideo/selectedVideoSlice';
import { getShareUrl } from '@/lib/routingUtils';
import { copyShareUrlImmediately } from '@/utils/clipboard';
import { RootState } from '@/reducers';
import { Member, FolderType, FileType } from '@/types';
import { screenAppAPIClient } from '@/lib/baseAxios';
import { fetchFolders } from '@/features/library/librarySlice';
import { AccessAvatarList } from './AccessAvatarList';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startInDetailedView?: boolean;
  folder?: FolderType | null;
  file?: FileType | null;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onOpenChange,
  startInDetailedView = false,
  folder: propFolder,
  file: propFile,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { teamMembers, currentUser, selectedVideo, team } = useSelector(
    (state: RootState) => ({
      teamMembers: state.team.selectedTeam?.teamSpaceMembers || [],
      currentUser: state.settings.user,
      selectedVideo: state.selectedVideo,
      team: state.team,
    }),
  );

  const file = propFile || (propFolder ? null : selectedVideo.file);
  const folder = propFolder;
  const teamId = team?.selectedTeam?.teamId;

  // Check if current user can invite to team (Owner, Admin, or TeamOwner)
  const canAddToTeam = React.useMemo(() => {
    if (!currentUser?._id || !team?.selectedTeam) return false;
    // Check if user is the team owner
    if (team.selectedTeam.ownerId === currentUser._id) return true;
    // Check member role
    const member = teamMembers.find((m) => m._id === currentUser._id);
    return (
      member?.role === 'Admin' ||
      member?.role === 'Owner' ||
      member?.role === 'TeamOwner'
    );
  }, [currentUser, team?.selectedTeam, teamMembers]);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [isCopied, setIsCopied] = React.useState(false);
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = React.useState<Member[]>([]);
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [message, setMessage] = React.useState('');
  const [notifyWhenAccessed, setNotifyWhenAccessed] = React.useState(false);
  const [addToTeam, setAddToTeam] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [publicLinkUrl, setPublicLinkUrl] = React.useState<string>('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Reset copied state after 3 seconds
  React.useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
        setCopyError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isCopied]);

  // Focus search input when opening in detailed mode
  React.useEffect(() => {
    if (open && startInDetailedView) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open, startInDetailedView]);

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedMembers([]);
      setSelectedEmails([]);
      setMessage('');
      setNotifyWhenAccessed(false);
      setAddToTeam(canAddToTeam);
      setIsCopied(false);
      setCopyError(null);
      setPublicLinkUrl('');
    }
  }, [open]);

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return '?';
  };

  const isValidEmail = (str: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(str);
  };

  const hasSearchInput = searchTerm.trim().length > 0;

  const filteredMembers = hasSearchInput
    ? teamMembers.filter(
        (member) =>
          member._id !== currentUser?._id &&
          !selectedMembers.some((m) => m._id === member._id) &&
          (member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    : [];

  const showEmailOption =
    hasSearchInput &&
    filteredMembers.length === 0 &&
    isValidEmail(searchTerm.trim()) &&
    !selectedEmails.includes(searchTerm.trim());

  const toggleMemberSelection = (member: Member) => {
    const isSelected = selectedMembers.some((m) => m._id === member._id);
    if (isSelected) {
      setSelectedMembers(selectedMembers.filter((m) => m._id !== member._id));
    } else {
      setSelectedMembers([...selectedMembers, member]);
      setSearchTerm('');
      searchInputRef.current?.focus();
    }
  };

  const handleCopyLink = async () => {
    if (!file?._id) return;

    try {
      const existingShareId = file?.share?.shareId;

      if (existingShareId) {
        // If file is already shared, copy immediately
        const result = await copyShareUrlImmediately(file._id, existingShareId);
        if (result.success) {
          setIsCopied(true);
          setCopyError(null);
          // Set the public link URL to display in input
          const shareUrl = getShareUrl(existingShareId);
          setPublicLinkUrl(shareUrl);
          toast.success(t('actions.general.copied'));
        } else {
          setCopyError(
            result.error ||
              t('selectedVideo.actions.modals.shareDialog.copyFailed'),
          );
          toast.error(
            result.error ||
              t('selectedVideo.actions.modals.shareDialog.copyFailed'),
          );
        }
      } else {
        // If not shared yet, share and copy
        const shareAction = await dispatch(shareThunk({ fileId: file._id }));
        const shareId = (shareAction as { payload?: { shareId?: string } })
          .payload?.shareId;
        if (shareId) {
          const shareUrl = getShareUrl(shareId);
          setPublicLinkUrl(shareUrl);
          setIsCopied(true);
          setCopyError(null);
          toast.success(t('actions.general.copied'));
        } else {
          throw new Error(
            t('selectedVideo.actions.modals.shareDialog.copyFailed'),
          );
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('selectedVideo.actions.modals.shareDialog.copyFailed');
      setCopyError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Check if there's a valid pending email in the search field
  const pendingEmail = searchTerm.trim();
  const hasPendingValidEmail =
    isValidEmail(pendingEmail) && !selectedEmails.includes(pendingEmail);

  // Check if all selected recipients are already team members
  // If so, hide the "Add to team" option since it's unnecessary
  const allSelectedAreTeamMembers = React.useMemo(() => {
    const allEmails = [
      ...selectedMembers.map((m) => m.email.toLowerCase()),
      ...selectedEmails.map((e) => e.toLowerCase()),
      ...(hasPendingValidEmail ? [pendingEmail.toLowerCase()] : []),
    ];
    if (allEmails.length === 0) return false;
    const teamMemberEmails = teamMembers.map((m) => m.email.toLowerCase());
    return allEmails.every((email) => teamMemberEmails.includes(email));
  }, [
    selectedMembers,
    selectedEmails,
    hasPendingValidEmail,
    pendingEmail,
    teamMembers,
  ]);

  const handleShareAndAddToTeam = async () => {
    try {
      setIsLoading(true);

      // Collect all emails to share with
      const emailsToShare: string[] = [];

      // Add selected team members' emails
      selectedMembers.forEach((member) => {
        emailsToShare.push(member.email);
      });

      // Add selected external emails
      selectedEmails.forEach((email) => {
        emailsToShare.push(email);
      });

      // Add pending valid email from search field if not already added
      if (hasPendingValidEmail) {
        emailsToShare.push(pendingEmail);
        setSearchTerm(''); // Clear the search field after adding
      }

      if (emailsToShare.length === 0) {
        return;
      }

      if (!teamId) {
        throw new Error(
          t('selectedVideo.actions.modals.shareDialog.errors.teamIdMissing'),
        );
      }

      const resources = [];
      if (file) {
        resources.push({
          type: 'File' as const,
          id: file._id,
        });
      } else if (folder) {
        resources.push({
          type: 'Folder' as const,
          id: folder._id,
        });
      }

      const response = await screenAppAPIClient.files.sendInvitation(teamId, {
        emails: emailsToShare,
        role: 'Member',
        message: message || undefined,
        notifyOnAccept: notifyWhenAccessed,
        resources: resources.length > 0 ? resources : undefined,
        addToTeam,
      });

      // Handle response and show appropriate toasts
      const resourceTypeKey = file ? 'common.file' : 'common.folder';
      const resourceType = t(resourceTypeKey);

      // Count successful invitations and unregistered users
      const successfulInvitations = response.data.invitations.filter(
        (inv) =>
          inv.status === 'created' ||
          inv.status === 'resent' ||
          inv.status === 'already_member' ||
          inv.status === 'import_created',
      );
      const unregisteredUsers = response.data.invitations.filter(
        (inv) => inv.status === 'user_not_registered',
      );

      // Show success toast for successful invitations
      if (successfulInvitations.length > 0) {
        toast.success(
          t('selectedVideo.actions.modals.shareByEmail.success', {
            count: successfulInvitations.length,
            resourceType,
            defaultValue_one:
              '{{resourceType}} shared with {{count}} recipient!',
            defaultValue_other:
              '{{resourceType}} shared with {{count}} recipients!',
          }),
        );
      }

      // Show warning toast for unregistered users
      if (unregisteredUsers.length > 0) {
        const emails = unregisteredUsers.map((inv) => inv.email).join(', ');
        toast.warning(
          t('selectedVideo.actions.modals.shareByEmail.errors.unregistered', {
            count: unregisteredUsers.length,
            emails,
            defaultValue_one:
              'Cannot invite {{emails}} - user must register first',
            defaultValue_other:
              'Cannot invite {{count}} users - they must register first: {{emails}}',
          }),
        );
      }

      // Refresh folders to update permissions/member list
      if (successfulInvitations.length > 0 && teamId) {
        dispatch(fetchFolders({ teamId }) as any);
      }

      // Close the dialog after sharing
      onOpenChange(false);
    } catch (error) {
      console.error('Share and add to team error:', error);

      // Show error toast
      toast.error(
        t('selectedVideo.actions.modals.shareByEmail.errors.failed', {
          resourceType: folder ? t('common.folder') : t('common.file'),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEmail = () => {
    const email = searchTerm.trim();
    if (isValidEmail(email) && !selectedEmails.includes(email)) {
      setSelectedEmails([...selectedEmails, email]);
      setSearchTerm('');
      searchInputRef.current?.focus();
    }
  };

  const handleRemoveEmail = (email: string) => {
    setSelectedEmails(selectedEmails.filter((e) => e !== email));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-lg font-semibold">
            {t('selectedVideo.actions.modals.shareDialog.titleFull')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Get Public Link button */}
          {!publicLinkUrl ? (
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="w-full"
              aria-label={t(
                'selectedVideo.actions.modals.shareDialog.getPublicLink',
              )}
            >
              <Copy className="h-4 w-4 mr-2" />
              {t('selectedVideo.actions.modals.shareDialog.getPublicLink')}
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={publicLinkUrl}
                  readOnly
                  className="flex-1"
                  aria-label={t(
                    'selectedVideo.actions.modals.shareDialog.publicLink',
                  )}
                />
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  aria-label={t('actions.general.copy')}
                >
                  {isCopied ? (
                    <Check className="h-4 w-4 stroke-[2.5]" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {copyError && (
                <p className="text-sm text-destructive">{copyError}</p>
              )}
            </div>
          )}

          {/* OR divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('common.or')}
              </span>
            </div>
          </div>

          {/* Add People section */}
          <div className="space-y-2.5">
            <Label className="text-sm font-medium">
              {t('selectedVideo.actions.modals.shareDialog.addPeople')}
            </Label>
            <div className="flex items-center gap-3">
              {file && (
                <AccessAvatarList
                  item={file}
                  size="sm"
                  hideShareDialog
                  onAddClick={() => searchInputRef.current?.focus()}
                />
              )}
              <Input
                ref={searchInputRef}
                type="text"
                placeholder={t(
                  'selectedVideo.actions.modals.shareDialog.searchByName',
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                className="flex-1"
                aria-label={t(
                  'selectedVideo.actions.modals.shareDialog.searchPlaceholder',
                )}
              />
            </div>
          </div>

          {/* Search results dropdown */}
          {hasSearchInput && (
            <div className="space-y-1 max-h-[200px] overflow-y-auto rounded-md border p-1">
              {filteredMembers.length > 0 &&
                filteredMembers.map((member) => (
                  <div
                    key={member._id}
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => toggleMemberSelection(member)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleMemberSelection(member);
                      }
                    }}
                  >
                    <Avatar className="h-8 w-8">
                      {member.picture ? (
                        <AvatarImage
                          src={member.picture}
                          alt={member.name || member.email}
                        />
                      ) : null}
                      <AvatarFallback className="text-xs">
                        {getInitials(member.name, member.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {member.name || t('common.unknown')}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </p>
                    </div>
                  </div>
                ))}

              {showEmailOption && (
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors hover:bg-accent"
                  onClick={handleAddEmail}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {getInitials('', searchTerm)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{searchTerm}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {t(
                        'selectedVideo.actions.modals.shareDialog.clickToAdd',
                        'Click to add',
                      )}
                    </p>
                  </div>
                </div>
              )}

              {!showEmailOption && filteredMembers.length === 0 && (
                <div className="p-2">
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'selectedVideo.actions.modals.shareDialog.noTeamMembersFound',
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Selected members and emails chips */}
          {(selectedMembers.length > 0 || selectedEmails.length > 0) && (
            <div className="space-y-2.5">
              <Label className="text-sm font-medium">
                {t('selectedVideo.actions.modals.shareDialog.selected')} (
                {selectedMembers.length + selectedEmails.length})
              </Label>
              <div className="max-h-[150px] overflow-y-auto rounded-md overflow-hidden">
                {selectedMembers.map((member, index) => (
                  <div
                    key={member._id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 bg-muted/50',
                      index > 0 && 'border-t border-border',
                    )}
                  >
                    <Avatar className="h-8 w-8">
                      {member.picture ? (
                        <AvatarImage
                          src={member.picture}
                          alt={member.name || member.email}
                        />
                      ) : null}
                      <AvatarFallback className="text-xs">
                        {getInitials(member.name, member.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {member.name || t('common.unknown')}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleMemberSelection(member)}
                      className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                      aria-label={t(
                        'selectedVideo.actions.modals.shareDialog.removeMember',
                        { name: member.name || member.email },
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}

                {selectedEmails.map((email, index) => (
                  <div
                    key={email}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 bg-muted/50',
                      (index > 0 || selectedMembers.length > 0) &&
                        'border-t border-border',
                    )}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {getInitials('', email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmail(email)}
                      className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                      aria-label={t(
                        'selectedVideo.actions.modals.shareDialog.removeEmail',
                        { email },
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message textarea */}
          <div className="space-y-2.5">
            <Label htmlFor="message" className="text-sm font-medium">
              {t('selectedVideo.actions.modals.shareDialog.messageLabel')}{' '}
              <span className="text-muted-foreground font-normal">
                ({t('common.optional')})
              </span>
            </Label>
            <Textarea
              id="message"
              placeholder={t(
                'selectedVideo.actions.modals.shareDialog.messagePlaceholder',
              )}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Options switches */}
          <div className="space-y-0 rounded-lg border overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2.5">
              <Label
                htmlFor="notify"
                className="cursor-pointer text-sm font-medium flex items-center gap-2"
              >
                <Bell className="h-4 w-4 text-muted-foreground" />
                {t(
                  'selectedVideo.actions.modals.shareDialog.notifyWhenAccessed',
                )}
              </Label>
              <Switch
                id="notify"
                checked={notifyWhenAccessed}
                onCheckedChange={setNotifyWhenAccessed}
              />
            </div>

            {canAddToTeam && !allSelectedAreTeamMembers && (
              <div className="flex items-center justify-between px-3 py-2.5 border-t">
                <Label
                  htmlFor="addToTeam"
                  className="cursor-pointer text-sm font-medium flex items-center gap-2"
                >
                  <Users className="h-4 w-4 text-muted-foreground" />
                  {t(
                    'selectedVideo.actions.modals.shareDialog.addToTeam',
                    'Add to team',
                  )}
                </Label>
                <Switch
                  id="addToTeam"
                  checked={addToTeam}
                  onCheckedChange={setAddToTeam}
                />
              </div>
            )}
          </div>

          {/* Share button */}
          <Button
            onClick={handleShareAndAddToTeam}
            disabled={
              (selectedMembers.length === 0 &&
                selectedEmails.length === 0 &&
                !hasPendingValidEmail) ||
              isLoading
            }
            className="w-full"
          >
            {(() => {
              if (isLoading) {
                return t(
                  'selectedVideo.actions.modals.shareDialog.sharingButton',
                );
              }
              if (addToTeam && !allSelectedAreTeamMembers) {
                return t(
                  'selectedVideo.actions.modals.shareDialog.shareAndAddToTeam',
                );
              }
              return t(
                'selectedVideo.actions.modals.shareDialog.share',
                'Share',
              );
            })()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
